/**
 * Import log for the Database page (the staff import - the only list still imported by hand, owner 28 Sep 2026).
 *
 * - `import_log/<table>`: one doc per table, overwritten on every apply, so the
 *   card shows the latest import with a single document read (no index).
 *   `history` holds the last 10 imports / checks of the table (newest first).
 *   `entry_id` is the id of the `import_log_entries` row that wrote it, which
 *   is also the id of its undo snapshot (`import_undo/<entry_id>`).
 * - `import_log_entries/<id>`: append-only history.
 */
import { db, auth } from '../firebase';
import { doc, getDoc, setDoc, collection, serverTimestamp } from 'firebase/firestore';
import logger from '../utils/logger';
import { USE_CONTRACTS_V2 } from './contractsV2';

/** The log lives in Firestore only: on the contracts store (the PostgreSQL switch) it is not kept at all - owner, 4 Oct
 *  2026: "سجل الاستيراد في صفحة Database: شيله" - nothing read, nothing written, nothing shown. */
export const IMPORT_LOG_ON = !USE_CONTRACTS_V2;

/** Entries kept in `import_log/<table>.history` (newest first). */
export const HISTORY_SIZE = 10;

export const importLogService = {
    /** Latest import for a table, or null. */
    async getLatest(table) {
        if (!IMPORT_LOG_ON) return null;
        try {
            const snap = await getDoc(doc(db, 'import_log', table));
            return snap.exists() ? snap.data() : null;
        } catch (error) {
            logger.error('[importLog] read failed:', error);
            return null;
        }
    },

    /**
     * Records an apply or a check. `counts` is the importer's stats object.
     * `history: false` only refreshes `import_log/<table>` (used by combined
     * imports that log one history entry for several tables).
     * `entryId` reuses an id claimed before the apply (the undo snapshot is
     * stored under it, see importUndoService); a fresh one is generated
     * otherwise. The id is written as `entry_id` on the table doc and on its
     * newest history row, so the undo can tell whether an import is still the
     * latest one of its table. `record` returns the entry with its `id`.
     */
    async record({ table, mode, fileName, counts, history = true, entryId = null }) {
        if (!IMPORT_LOG_ON) return { id: entryId || null, table, mode: mode || null };
        const previous = await this.getLatest(table);

        const id = entryId || doc(collection(db, 'import_log_entries')).id;
        const entry = {
            table,
            mode: mode || null,
            fileName: fileName || null,
            user: auth.currentUser?.email || null,
            at: new Date().toISOString(),
            counts: counts || {},
            entry_id: id,
            last_updated: serverTimestamp()
        };
        // Last HISTORY_SIZE imports / checks of the table, newest first, kept inside the
        // table doc so the importer page lists them with the same single read (no index).
        const recent = { at: entry.at, user: entry.user, mode: entry.mode, fileName: entry.fileName, counts: entry.counts, entry_id: id };
        const previousHistory = Array.isArray(previous?.history) ? previous.history : [];
        await setDoc(doc(db, 'import_log', table), { ...entry, history: [recent, ...previousHistory].slice(0, HISTORY_SIZE) });
        if (history) await setDoc(doc(db, 'import_log_entries', id), entry);
        return { ...entry, id };
    }
};
